import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Plus, Search, Wrench, AlertTriangle, DollarSign, Shield,
  X, Trash2, ImagePlus, Calendar, Gauge, TrendingUp, TrendingDown, CheckCircle2,
  Filter,
} from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";

const TOKEN_KEY = "vf_admin_token";

const fmtAUD = (n) =>
  n == null ? "—" : new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 0 }).format(n);

const TYPE_META = {
  service: { icon: Wrench, tone: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30", label: "SERVICE" },
  damage: { icon: AlertTriangle, tone: "text-red-400 bg-red-500/10 border-red-500/30", label: "DAMAGE" },
  cost: { icon: DollarSign, tone: "text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/30", label: "COST" },
  warranty: { icon: Shield, tone: "text-blue-400 bg-blue-500/10 border-blue-500/30", label: "WARRANTY" },
};

const STATUS_BADGE = {
  logged: "bg-white/10 text-white/60 border-white/20",
  pending_review: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  approved: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  rejected: "bg-white/5 text-white/40 border-white/10",
};

export default function AdminMaintenance() {
  const [searchParams] = useSearchParams();
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [vehicleFilter, setVehicleFilter] = useState(searchParams.get("vehicle_id") || "");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [viewEntry, setViewEntry] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [eRes, sRes, vRes] = await Promise.all([
        api.get("/admin/maintenance", {
          params: {
            type: typeFilter, status: statusFilter,
            vehicle_id: vehicleFilter || undefined,
            q: q || undefined,
          },
        }),
        api.get("/admin/maintenance/summary"),
        api.get("/admin/rto/fleet"),
      ]);
      setEntries(eRes.data.filter((e) => !e.deleted));
      setSummary(sRes.data);
      setVehicles(vRes.data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setLoading(false); }
  }, [typeFilter, statusFilter, vehicleFilter, q]);

  useEffect(() => { load(); }, [load]);

  const deleteEntry = async (id) => {
    if (!window.confirm("Delete this maintenance entry?")) return;
    try {
      await api.delete(`/admin/maintenance/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/maintenance/${id}`, { status });
      toast.success(`Marked ${status.replace("_", " ")}`);
      load();
      if (viewEntry?.id === id) setViewEntry({ ...viewEntry, status });
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-maintenance-page" className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 bg-[#080808] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
          <Logo />
          <Link
            to="/admin"
            data-testid="admin-maintenance-back"
            className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-[#D4AF37] tracking-wide"
          ><ArrowLeft size={14} /> Back to Dashboard</Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-8 py-10">
        <div className="label-eyebrow mb-3">RTO · Maintenance & Damage Log</div>
        <h1 className="font-display text-4xl md:text-5xl mb-6" style={{ fontWeight: 300 }}>
          Maintenance <span className="italic text-[#D4AF37]">Console</span>
        </h1>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <SummaryCard label="TOTAL SPEND" primary={fmtAUD(summary?.total_spend ?? 0)} />
          <SummaryCard label="ENTRIES" primary={summary?.total_entries ?? 0} />
          <SummaryCard
            label="OVERDUE SERVICE"
            primary={summary?.overdue_services?.length ?? 0}
            tone={(summary?.overdue_services?.length ?? 0) > 0 ? "warn" : undefined}
          />
          <SummaryCard
            label="PENDING DAMAGE"
            primary={summary?.pending_damage_reports ?? 0}
            tone={(summary?.pending_damage_reports ?? 0) > 0 ? "warn" : undefined}
          />
        </div>

        {/* Overdue callout */}
        {summary?.overdue_services?.length > 0 && (
          <div className="border border-red-500/30 bg-red-500/5 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-red-300 text-[10px] tracking-widest mb-2">
              <AlertTriangle size={12} /> OVERDUE SERVICES
            </div>
            <div className="flex flex-wrap gap-2">
              {summary.overdue_services.map((v) => (
                <span
                  key={v.id}
                  onClick={() => setVehicleFilter(v.id)}
                  className="text-xs bg-white/5 border border-white/10 rounded px-2 py-1 cursor-pointer hover:border-[#D4AF37]/40"
                >
                  {v.year} {v.make} {v.model} · {v.rego} · due {v.next_service_date}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <button
            onClick={() => setShowAdd(true)}
            data-testid="admin-maintenance-add"
            className="inline-flex items-center gap-1.5 bg-[#D4AF37] text-[#050505] font-semibold rounded px-4 py-2 text-xs tracking-widest hover:bg-[#E5C158]"
          ><Plus size={13} /> ADD ENTRY</button>

          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                data-testid="admin-maintenance-search"
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search category, vendor…"
                className="bg-[#0a0a0a] border border-white/10 rounded pl-8 pr-3 py-1.5 text-xs w-64 focus:outline-none focus:border-[#D4AF37]/50"
              />
            </div>
            <select
              data-testid="admin-maintenance-type-filter"
              value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
              className="bg-[#0a0a0a] border border-white/10 rounded px-3 py-1.5 text-xs"
            >
              {["all", "service", "damage", "cost", "warranty"].map((s) =>
                <option key={s} value={s}>{s.toUpperCase()}</option>
              )}
            </select>
            <select
              data-testid="admin-maintenance-status-filter"
              value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-[#0a0a0a] border border-white/10 rounded px-3 py-1.5 text-xs"
            >
              {["all", "logged", "pending_review", "approved", "rejected"].map((s) =>
                <option key={s} value={s}>{s.replace("_", " ").toUpperCase()}</option>
              )}
            </select>
            <select
              data-testid="admin-maintenance-vehicle-filter"
              value={vehicleFilter} onChange={(e) => setVehicleFilter(e.target.value)}
              className="bg-[#0a0a0a] border border-white/10 rounded px-3 py-1.5 text-xs max-w-[220px]"
            >
              <option value="">All vehicles</option>
              {vehicles.map((v) =>
                <option key={v.id} value={v.id}>{v.rego} · {v.make} {v.model}</option>
              )}
            </select>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-white/40 text-sm">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="border border-white/10 border-dashed rounded-lg p-10 text-center text-white/40 text-sm">
            No maintenance entries yet — add one with the button above.
          </div>
        ) : (
          <div className="border border-white/10 rounded-lg overflow-hidden overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="bg-white/5 text-[10px] tracking-widest text-white/50">
                  <th className="text-left px-3 py-3">DATE</th>
                  <th className="text-left px-3 py-3">TYPE</th>
                  <th className="text-left px-3 py-3">VEHICLE</th>
                  <th className="text-left px-3 py-3">CATEGORY</th>
                  <th className="text-left px-3 py-3">VENDOR</th>
                  <th className="text-right px-3 py-3">KM</th>
                  <th className="text-right px-3 py-3">AMOUNT</th>
                  <th className="text-left px-3 py-3">STATUS</th>
                  <th className="text-right px-3 py-3">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const Meta = TYPE_META[e.type] || TYPE_META.cost;
                  const Icon = Meta.icon;
                  return (
                    <tr
                      key={e.id}
                      className="border-t border-white/10 hover:bg-white/[0.02] cursor-pointer"
                      onClick={() => setViewEntry(e)}
                      data-testid={`admin-maintenance-row-${e.id}`}
                    >
                      <td className="px-3 py-3">{e.date}</td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] tracking-widest ${Meta.tone}`}>
                          <Icon size={10} /> {Meta.label}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <div className="text-white">{e.vehicle_label}</div>
                        <div className="text-xs text-white/40">{e.vehicle_rego}</div>
                      </td>
                      <td className="px-3 py-3 text-white/80">{e.category}</td>
                      <td className="px-3 py-3 text-white/60">{e.vendor || "—"}</td>
                      <td className="px-3 py-3 text-right text-white/60">{e.odometer_km ? e.odometer_km.toLocaleString() : "—"}</td>
                      <td className="px-3 py-3 text-right">{fmtAUD(e.amount)}</td>
                      <td className="px-3 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded border text-[10px] tracking-widest uppercase ${STATUS_BADGE[e.status] || ""}`}>
                          {e.status.replace("_", " ")}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <button
                          onClick={(ev) => { ev.stopPropagation(); deleteEntry(e.id); }}
                          data-testid={`admin-maintenance-delete-${e.id}`}
                          className="text-white/60 hover:text-red-400"
                        ><Trash2 size={13} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Per-vehicle P&L */}
        {summary?.per_vehicle?.length > 0 && (
          <div className="mt-10">
            <h2 className="text-white/80 tracking-wide text-sm mb-3">PER-VEHICLE RUNNING COSTS & PROFITABILITY</h2>
            <div className="border border-white/10 rounded-lg overflow-hidden overflow-x-auto">
              <table className="w-full text-sm min-w-[700px]">
                <thead>
                  <tr className="bg-white/5 text-[10px] tracking-widest text-white/50">
                    <th className="text-left px-3 py-3">VEHICLE</th>
                    <th className="text-right px-3 py-3">ENTRIES</th>
                    <th className="text-right px-3 py-3">SPEND</th>
                    <th className="text-right px-3 py-3">REVENUE</th>
                    <th className="text-right px-3 py-3">NET</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.per_vehicle.map((v) => (
                    <tr key={v.vehicle_id} className="border-t border-white/10">
                      <td className="px-3 py-3">
                        <div className="text-white">{v.label}</div>
                        <div className="text-xs text-white/40">{v.rego}</div>
                      </td>
                      <td className="px-3 py-3 text-right text-white/70">{v.count}</td>
                      <td className="px-3 py-3 text-right text-red-300">{fmtAUD(v.total)}</td>
                      <td className="px-3 py-3 text-right text-emerald-400">{fmtAUD(v.revenue)}</td>
                      <td className="px-3 py-3 text-right">
                        <span className={v.net >= 0 ? "text-emerald-400" : "text-red-300"}>
                          {v.net >= 0 ? <TrendingUp size={11} className="inline" /> : <TrendingDown size={11} className="inline" />}
                          {" "}{fmtAUD(v.net)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Monthly spend */}
        {summary?.monthly?.length > 0 && (
          <div className="mt-10">
            <h2 className="text-white/80 tracking-wide text-sm mb-3">MONTHLY SPEND — LAST 12 MONTHS</h2>
            <div className="flex items-end gap-2 h-40 border border-white/10 rounded-lg p-5 bg-[#0a0a0a]">
              {summary.monthly.map((m) => {
                const max = Math.max(...summary.monthly.map((x) => x.total), 1);
                const h = Math.max(6, (m.total / max) * 100);
                return (
                  <div key={m.month} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                    <div className="text-[10px] text-white/60 truncate">{fmtAUD(m.total)}</div>
                    <div className="w-full bg-[#D4AF37]/60 hover:bg-[#D4AF37] rounded-t transition" style={{ height: `${h}%` }} />
                    <div className="text-[9px] text-white/40 tracking-wider">{m.month.slice(5)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>

      {showAdd && (
        <AddMaintenanceModal
          vehicles={vehicles}
          onClose={() => setShowAdd(false)}
          onCreated={() => { setShowAdd(false); load(); }}
        />
      )}
      {viewEntry && (
        <EntryDrawer
          entry={viewEntry}
          onClose={() => setViewEntry(null)}
          onSetStatus={setStatus}
        />
      )}
    </div>
  );
}

function SummaryCard({ label, primary, tone }) {
  return (
    <div className="border border-white/10 rounded-lg p-4 bg-[#0a0a0a]">
      <div className="text-[10px] tracking-widest text-white/50 mb-1">{label}</div>
      <div className={`font-display text-2xl ${tone === "warn" ? "text-red-300" : "text-white"}`} style={{ fontWeight: 300 }}>
        {primary}
      </div>
    </div>
  );
}

function AddMaintenanceModal({ vehicles, onClose, onCreated }) {
  const [form, setForm] = useState({
    vehicle_id: "", type: "service", category: "",
    date: new Date().toISOString().slice(0, 10),
    odometer_km: "", amount: "", vendor: "", description: "",
    next_service_date: "", next_service_km: "",
  });
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const overlayRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id || !form.category || !form.date) return toast.error("Vehicle, category and date required");
    setSaving(true);
    const fd = new FormData();
    fd.append("vehicle_id", form.vehicle_id);
    fd.append("type", form.type);
    fd.append("category", form.category);
    fd.append("date", form.date);
    if (form.odometer_km) fd.append("odometer_km", form.odometer_km);
    if (form.amount) fd.append("amount", form.amount);
    if (form.vendor) fd.append("vendor", form.vendor);
    if (form.description) fd.append("description", form.description);
    if (form.next_service_date) fd.append("next_service_date", form.next_service_date);
    if (form.next_service_km) fd.append("next_service_km", form.next_service_km);
    files.forEach((f) => fd.append("photos", f));

    try {
      await api.post("/admin/maintenance", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Entry added");
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const CATEGORY_SUGGESTIONS = {
    service: ["Log-book service", "Oil change", "Tyres", "Brakes", "Rego renewal", "Pink slip", "eSafety check"],
    damage: ["Dent", "Scratch", "Accident", "Windshield", "Panel repair", "Insurance claim"],
    cost: ["Fuel", "Detailing", "Cleaning", "Toll admin", "Fine", "Parking"],
    warranty: ["Recall", "Warranty claim", "Manufacturer notice"],
  };

  return (
    <div
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      className="fixed inset-0 bg-black/70 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto"
    >
      <div data-testid="admin-maintenance-add-modal" className="bg-[#0a0a0a] border border-white/10 rounded-lg w-full max-w-2xl my-4">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <div className="text-[#D4AF37] text-[10px] tracking-widest">MAINTENANCE LOG</div>
            <div className="font-display text-xl">New entry</div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="admin-maintenance-add-close"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <ModalField label="VEHICLE">
            <select
              data-testid="admin-maintenance-add-vehicle"
              required
              value={form.vehicle_id}
              onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })}
              className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm"
            >
              <option value="">— Select vehicle —</option>
              {vehicles.map((v) =>
                <option key={v.id} value={v.id}>{v.rego} · {v.year} {v.make} {v.model}</option>
              )}
            </select>
          </ModalField>

          <div className="grid grid-cols-2 gap-3">
            <ModalField label="TYPE">
              <select
                data-testid="admin-maintenance-add-type"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value, category: "" })}
                className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm"
              >
                {["service", "damage", "cost", "warranty"].map(t =>
                  <option key={t} value={t}>{t.toUpperCase()}</option>
                )}
              </select>
            </ModalField>
            <ModalField label="DATE">
              <input
                data-testid="admin-maintenance-add-date"
                type="date" required
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm"
              />
            </ModalField>
          </div>

          <ModalField label="CATEGORY">
            <input
              data-testid="admin-maintenance-add-category"
              required
              list="cat-suggestions"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder="e.g. Log-book service"
              className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm"
            />
            <datalist id="cat-suggestions">
              {(CATEGORY_SUGGESTIONS[form.type] || []).map((s) =>
                <option key={s} value={s} />
              )}
            </datalist>
          </ModalField>

          <div className="grid grid-cols-3 gap-3">
            <ModalField label="ODOMETER (KM)">
              <input
                data-testid="admin-maintenance-add-km"
                type="number" min="0"
                value={form.odometer_km}
                onChange={(e) => setForm({ ...form, odometer_km: e.target.value })}
                className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm"
              />
            </ModalField>
            <ModalField label="AMOUNT (AUD)">
              <input
                data-testid="admin-maintenance-add-amount"
                type="number" step="0.01" min="0"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm"
              />
            </ModalField>
            <ModalField label="VENDOR">
              <input
                data-testid="admin-maintenance-add-vendor"
                value={form.vendor}
                onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                placeholder="e.g. Bob's Automotive"
                className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm"
              />
            </ModalField>
          </div>

          <ModalField label="DESCRIPTION / NOTES">
            <textarea
              data-testid="admin-maintenance-add-description"
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm"
            />
          </ModalField>

          {form.type === "service" && (
            <div className="grid grid-cols-2 gap-3 border-t border-white/10 pt-4">
              <ModalField label="NEXT SERVICE DUE (DATE)">
                <input
                  data-testid="admin-maintenance-add-next-date"
                  type="date"
                  value={form.next_service_date}
                  onChange={(e) => setForm({ ...form, next_service_date: e.target.value })}
                  className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm"
                />
              </ModalField>
              <ModalField label="NEXT SERVICE DUE (KM)">
                <input
                  data-testid="admin-maintenance-add-next-km"
                  type="number" min="0"
                  value={form.next_service_km}
                  onChange={(e) => setForm({ ...form, next_service_km: e.target.value })}
                  className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm"
                />
              </ModalField>
            </div>
          )}

          <ModalField label={`PHOTOS (max 10, up to 8 MB each)`}>
            <label className="border border-dashed border-white/15 rounded-lg p-4 flex items-center gap-2 text-white/60 hover:border-[#D4AF37]/40 cursor-pointer">
              <ImagePlus size={14} className="text-[#D4AF37]" />
              <span className="text-xs">
                {files.length > 0 ? `${files.length} file(s) selected` : "Click to choose images"}
              </span>
              <input
                data-testid="admin-maintenance-add-photos"
                type="file" multiple accept="image/*"
                className="hidden"
                onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 10))}
              />
            </label>
          </ModalField>

          <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
            <button type="button" onClick={onClose} className="px-4 py-2 text-xs tracking-widest text-white/70 hover:text-white">CANCEL</button>
            <button
              type="submit"
              data-testid="admin-maintenance-add-submit"
              disabled={saving}
              className="bg-[#D4AF37] text-[#050505] font-semibold rounded px-5 py-2 text-xs tracking-widest hover:bg-[#E5C158] disabled:opacity-50"
            >{saving ? "SAVING…" : "ADD ENTRY"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EntryDrawer({ entry, onClose, onSetStatus }) {
  const overlayRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const Meta = TYPE_META[entry.type] || TYPE_META.cost;
  const token = localStorage.getItem(TOKEN_KEY);

  return (
    <div
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      className="fixed inset-0 bg-black/70 z-50 flex justify-end"
    >
      <div data-testid="admin-maintenance-drawer" className="relative w-full max-w-2xl h-full bg-[#080808] border-l border-white/10 overflow-y-auto">
        <div className="sticky top-0 bg-[#080808] border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] tracking-widest ${Meta.tone}`}>
              {Meta.label}
            </div>
            <div className="text-white font-mono mt-1">{entry.vehicle_label} · {entry.vehicle_rego}</div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Info label="CATEGORY" value={entry.category} />
            <Info label="DATE" value={entry.date} />
            <Info label="ODOMETER" value={entry.odometer_km ? `${entry.odometer_km.toLocaleString()} km` : "—"} />
            <Info label="AMOUNT" value={fmtAUD(entry.amount)} />
            <Info label="VENDOR" value={entry.vendor || "—"} />
            <Info label="REPORTED BY" value={entry.reported_by === "customer" ? `Customer · ${entry.customer_name}` : "Admin"} />
            {entry.type === "service" && entry.next_service_date && (
              <Info label="NEXT SERVICE DATE" value={entry.next_service_date} />
            )}
            {entry.type === "service" && entry.next_service_km && (
              <Info label="NEXT SERVICE KM" value={`${entry.next_service_km.toLocaleString()} km`} />
            )}
          </div>

          {entry.description && (
            <div>
              <div className="text-[10px] tracking-widest text-white/40 mb-1">DESCRIPTION</div>
              <div className="text-sm text-white/90 whitespace-pre-wrap bg-white/[0.03] rounded p-3 border border-white/5">
                {entry.description}
              </div>
            </div>
          )}

          {entry.photos?.length > 0 && (
            <div>
              <div className="text-[10px] tracking-widest text-white/40 mb-2">PHOTOS ({entry.photos.length})</div>
              <div className="grid grid-cols-3 gap-2">
                {entry.photos.map((p, idx) => (
                  <a
                    key={idx}
                    href={`${API_BASE}/maintenance/photos/${entry.id}/${idx}?auth=${token}`}
                    target="_blank" rel="noopener noreferrer"
                    className="block aspect-square bg-white/5 rounded overflow-hidden border border-white/10 hover:border-[#D4AF37]/40"
                  >
                    <img
                      src={`${API_BASE}/maintenance/photos/${entry.id}/${idx}?auth=${token}`}
                      alt={p.filename}
                      className="w-full h-full object-cover"
                    />
                  </a>
                ))}
              </div>
            </div>
          )}

          {entry.status === "pending_review" && (
            <div className="flex gap-2 pt-4 border-t border-white/10">
              <button
                onClick={() => onSetStatus(entry.id, "approved")}
                data-testid={`admin-maintenance-approve-${entry.id}`}
                className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded px-4 py-2 text-xs tracking-widest hover:bg-emerald-500/30"
              >APPROVE</button>
              <button
                onClick={() => onSetStatus(entry.id, "rejected")}
                data-testid={`admin-maintenance-reject-${entry.id}`}
                className="bg-red-500/20 text-red-300 border border-red-500/30 rounded px-4 py-2 text-xs tracking-widest hover:bg-red-500/30"
              >REJECT</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ModalField({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-widest text-white/50 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] tracking-widest text-white/40">{label}</div>
      <div className="text-sm text-white/90 mt-0.5">{value}</div>
    </div>
  );
}
